import { DEFAULT_JOB_AREA, JOB_AREA_TASKS } from './content/jobAreaTasks';
import { ENTRY_LEVEL_SHRINKING, JOBS_IMPACTED } from './content/stats';
import { NON_ANSWERS } from './scoring';

export const FUNNEL_FIELDS = { funnelName: 'ai', funnelVersion: '6' };

const icon = (name) => `/assets/images/question-answers/${name}.png`;
const platform = (name) => icon(`ai-platforms/${name}`);

const STUDENT_JOB_AREA = 'Student or looking for work';

const WORRY_HEADLINES = {
  'Very worried': 'You’re right to be worried.',
  'A little worried': 'That worry is real.',
  'Not really': 'You’re not worried. Employers are planning anyway.',
  "I haven't thought about it": 'You haven’t thought about it. Employers have.',
};

const AGENT_HEADLINES = {
  'Yes, a lot': 'You already have this edge. Keep it.',
  'Yes, a little': 'You’ve started. Now make it count.',
  'I tried but stopped': 'You stopped. Others kept going.',
  No: 'While you sleep, others’ agents are working.',
  "I didn't know it could": 'Now you know. Others already do.',
};

const MANY_TOOLS = 5;

const getIcebergHeadline = (toolCount) => {
  if (toolCount === 0) return 'You haven’t started. There’s a lot under the surface.';
  if (toolCount >= MANY_TOOLS) return 'You use plenty of tools. But can you use what’s underneath?';
  return 'You’re only seeing the tip.';
};

const realTools = (answer) =>
  Array.isArray(answer) ? answer.filter((item) => !NON_ANSWERS.includes(item)) : [];

const funnelSteps = [
  {
    type: 'landing',
    slug: '',
    showProgress: false,
    data: {
      id: 'job-area',
      questionType: 'single',
      question: 'What kind of work do you do?',
      options: Object.keys(JOB_AREA_TASKS),
    },
  },

  {
    type: 'question',
    slug: 'question/1',
    showProgress: true,
    data: {
      id: 'age',
      questionType: 'single',
      question: 'How old are you?',
      description: 'This helps us work out your score.',
      layout: 'two-columns',
      options: ['18-24', '25-34', '35-44', '45+'],
    },
  },

  {
    type: 'question',
    slug: 'question/2',
    showProgress: true,
    data: {
      id: 'job-worry',
      questionType: 'single',
      question: 'Are you worried AI could take your job?',
      options: ['Very worried', 'A little worried', 'Not really', "I haven't thought about it"],
      optionImages: [icon('anxious'), icon('confused'), icon('smiling'), icon('thinking-face')],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'reality-check',
    slug: 'reality-check/1',
    showProgress: true,
    data: {
      id: 'employers-reducing',
      getContent: (answers) => ({
        headline: WORRY_HEADLINES[answers['job-worry']] || 'This is already happening.',
        visual: JOB_AREA_TASKS[answers['job-area']] || JOB_AREA_TASKS[DEFAULT_JOB_AREA],
        footnote: answers['job-area'] === STUDENT_JOB_AREA ? ENTRY_LEVEL_SHRINKING : JOBS_IMPACTED,
      }),
    },
  },

  {
    type: 'question',
    slug: 'question/3',
    showProgress: true,
    data: {
      id: 'ai-at-work',
      questionType: 'single',
      question: 'How often do you use AI at work?',
      options: ['Every day', 'Every week', 'Not often', 'Never'],
      optionImages: [icon('rocket'), icon('thumbs-up'), icon('person-shrugging'), icon('prohibited')],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'question',
    slug: 'question/4',
    showProgress: true,
    data: {
      id: 'ai-personal',
      questionType: 'multi',
      question: 'What do you use AI for outside of work?',
      description: 'Pick all that fit',
      exclusiveOptions: NON_ANSWERS,
      options: [
        'Writing',
        'Asking questions',
        'Making plans',
        'Learning new things',
        'Health or money questions',
        'None of these',
      ],
      optionImages: [
        icon('writing-hand'),
        icon('magnifying-glass'),
        icon('memo'),
        icon('books'),
        icon('heart'),
        icon('prohibited'),
      ],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'question',
    slug: 'question/5',
    showProgress: true,
    data: {
      id: 'ai-tools',
      questionType: 'multi',
      question: 'Which AI tools do you use?',
      description: 'Pick all that fit',
      exclusiveOptions: NON_ANSWERS,
      options: [
        'ChatGPT',
        'Claude',
        'Gemini',
        'Grok',
        'DeepSeek',
        'Copilot',
        'Perplexity',
        'NotebookLM',
        'Midjourney',
        'Cursor',
        'Claude Code',
        'Codex',
        'Zapier / n8n',
        'None of these',
      ],
      optionImages: [
        platform('chatgpt'),
        platform('claude'),
        platform('gemini'),
        platform('grok'),
        platform('deepseek'),
        platform('copilot'),
        platform('perplexity'),
        platform('notebooklm'),
        platform('midjourney'),
        platform('cursor'),
        platform('claude-code'),
        platform('codex'),
        platform('n8n'),
        icon('prohibited'),
      ],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'question',
    slug: 'question/6',
    showProgress: true,
    data: {
      id: 'ai-models',
      questionType: 'single',
      question: 'Do you choose which AI model to use?',
      description: 'The model is the AI inside an app. For example, ChatGPT runs on GPT.',
      options: [
        'Yes, I pick one for each task',
        'Sometimes I switch',
        'No, I use the default',
        "I don't know what a model is",
      ],
      optionImages: [icon('rocket'), icon('thumbs-up'), icon('person-shrugging'), icon('question-mark')],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'reality-check',
    slug: 'reality-check/2',
    showProgress: true,
    data: {
      id: 'model-vs-app',
      getContent: (answers) => {
        const tools = realTools(answers['ai-tools']);
        return {
          headline: getIcebergHeadline(tools.length),
          subhead: 'ChatGPT is an app. GPT is the model inside it.',
          visual: { tools },
        };
      },
    },
  },

  {
    type: 'question',
    slug: 'question/7',
    showProgress: true,
    data: {
      id: 'ai-explain',
      questionType: 'multi',
      question: 'Which of these could you explain to a friend?',
      description: 'Be honest. Only pick the ones you really know.',
      exclusiveOptions: NON_ANSWERS,
      options: [
        'What a harness is',
        'AI agents',
        'AI skills',
        'Plugins',
        'AI workflows',
        'None of these',
      ],
    },
  },

  {
    type: 'question',
    slug: 'question/8',
    showProgress: true,
    data: {
      id: 'ai-connectors',
      questionType: 'single',
      question: 'Can your AI see your email, calendar or files?',
      options: ['Yes, all of them', 'Yes, some of them', 'No', "I didn't know it could"],
      optionImages: [icon('rocket'), icon('thumbs-up'), icon('thumbs-down'), icon('confused')],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'question',
    slug: 'question/9',
    showProgress: true,
    data: {
      id: 'ai-skills',
      questionType: 'single',
      question: 'Have you set up skills or rules for your AI?',
      options: ['Yes, I use them a lot', 'Yes, a few simple rules', 'No', "What's a skill?"],
      optionImages: [icon('rocket'), icon('thumbs-up'), icon('thumbs-down'), icon('question-mark')],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'question',
    slug: 'question/10',
    showProgress: true,
    data: {
      id: 'ai-agents',
      questionType: 'single',
      question: "Does AI work for you while you're away?",
      description: 'For example, it sorts your email every morning.',
      options: ['Yes, a lot', 'Yes, a little', 'I tried but stopped', 'No', "I didn't know it could"],
      optionImages: [
        icon('rocket'),
        icon('thumbs-up'),
        icon('person-shrugging'),
        icon('thumbs-down'),
        icon('confused'),
      ],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'reality-check',
    slug: 'reality-check/3',
    showProgress: true,
    data: {
      id: 'scheduled-agents',
      getContent: (answers) => ({
        headline: AGENT_HEADLINES[answers['ai-agents']] || AGENT_HEADLINES.No,
      }),
    },
  },

  { type: 'readiness', slug: 'readiness', showProgress: false },

  {
    type: 'question',
    slug: 'question/11',
    showProgress: true,
    data: {
      id: 'repetitive-hours',
      questionType: 'single',
      question: 'How many hours a week go to the same boring tasks?',
      options: ['Less than 2 hours', '2-5 hours', '5-10 hours', '10+ hours'],
      optionImages: [icon('clock'), icon('hourglass-not-done'), icon('alarm-clock'), icon('sleeping-face')],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'question',
    slug: 'question/12',
    showProgress: true,
    data: {
      id: 'keep-up',
      questionType: 'single',
      question: 'How do you learn about new AI tools?',
      options: ['I follow AI news', 'I see some posts or videos', 'People at work tell me', "I don't look for them"],
      optionImages: [icon('rocket'), icon('magnifying-glass'), icon('briefcase'), icon('pensive')],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'question',
    slug: 'question/13',
    showProgress: true,
    data: {
      id: 'main-goal',
      questionType: 'single',
      question: 'What is your main goal?',
      options: [
        'Keep my job safe',
        'Get a better job or more pay',
        "Move to a job AI can't take",
        'Let AI do my boring tasks',
      ],
      optionImages: [icon('target'), icon('chart-increasing'), icon('briefcase'), icon('gear')],
      optionImagesSize: '32px',
    },
  },

  {
    type: 'question',
    slug: 'question/14',
    showProgress: true,
    data: {
      id: 'learning-minutes',
      questionType: 'single',
      question: 'How many minutes a day can you learn?',
      layout: 'two-columns',
      options: ['5 minutes', '10 minutes', '15 minutes', '20+ minutes'],
    },
  },

  { type: 'confident', slug: 'confident', showProgress: true },
  { type: 'ai-team', slug: 'ai-team', showProgress: true },
  { type: 'calculating', slug: 'calculating-results', showProgress: false },
  { type: 'email', slug: 'email', showProgress: false },
  { type: 'transition', slug: 'transition', showProgress: false },
  { type: 'checkout', slug: 'checkout', showProgress: false },
  { type: 'special-offer', slug: 'special-offer', showProgress: false },
  { type: 'upgrade', slug: 'upgrade', showProgress: false },
  { type: 'success', slug: 'success', showProgress: false },
];

export default funnelSteps;
